import { DETAIL_ROUTES, detailRoutePath, type DetailRouteKey } from "./detailRoutes.js";
import type { AuditEvent, EntityReferenceId } from "./types.js";
import type { Tab } from "../features/shell/navigation.js";
import { financeSectionPath } from "../features/finance/sections.js";

export type EntityRouteTarget = {
  path: string;
  tab: Tab;
  label: string;
};

function routeId(value: unknown): string {
  return encodeURIComponent(String(value));
}

function metadataId(metadata: Record<string, unknown> | undefined, ...keys: string[]): EntityReferenceId | null {
  if (!metadata) return null;
  for (const key of keys) {
    const value = metadata[key];
    if (typeof value === "string" && value.trim()) return value;
    if (typeof value === "number" && Number.isInteger(value) && value > 0) return String(value);
  }
  return null;
}

function labelFor(entityType: string): string {
  return entityType.replaceAll("_", " ");
}

/**
 * A deep link to one of the query-parameter detail views (#533). The path and
 * the parameter name come from `detailRoutes.ts`, which is also what the view
 * itself writes when the reader opens that object — this module used to build
 * the same strings by hand, which made it a second, silently divergent
 * definition of the shape it publishes. Without an id it is the index, the
 * degradation these routes have always taken in preference to a dead link.
 */
function detailTarget(key: DetailRouteKey, id: EntityReferenceId | null, label: string): EntityRouteTarget {
  return { path: detailRoutePath(key, id), tab: DETAIL_ROUTES[key].tab, label };
}


export function routeForEntity(
  entityType: string,
  entityId?: EntityReferenceId | null,
  metadata?: Record<string, unknown>
): EntityRouteTarget | null {
  const id = entityId == null ? "" : String(entityId);
  const label = labelFor(entityType);

  // A place and a thing are both `asset` rows since #1050, and they have
  // DIFFERENT pages. The id cannot tell them apart, so recordAudit leaves the
  // caller's declared spelling in metadata whenever canonicalisation changed
  // it, and routing trusts that over the stored column. Without this a place's
  // stock-add event opened the thing detail page and got "Thing not found".
  const declaredEntityType = typeof metadata?.declaredEntityType === "string" ? metadata.declaredEntityType : "";
  if ((declaredEntityType === "location" || entityType === "location") && id) {
    return { path: `/inventory/places/${routeId(id)}`, tab: "inventory", label: "place" };
  }

  if (entityType === "asset" && id) return { path: `/inventory/assets/${routeId(id)}`, tab: "inventory", label };
  // A listing has no page of its own (#1184) — it lives in the sale section of
  // the thing being sold — so its audit events open that thing. `assetId` is
  // written into the metadata of every listing audit row for this. Without the
  // id it degrades to the Things list rather than a dead link, the same
  // degradation the search route takes.
  if (entityType === "asset_listing") {
    const assetId = metadataId(metadata, "assetId");
    return assetId
      ? { path: `/inventory/assets/${routeId(assetId)}`, tab: "inventory", label }
      : { path: "/inventory", tab: "inventory", label };
  }
  // #1185: the wishlist is a screen under Things which reads `?screen=` and
  // validates it against its own tabs. No per-row anchor, for the vendor
  // directory's reason — the list renders after its fetch, so a hash would be a
  // promise the page cannot keep.
  if (entityType === "asset_acquisition") return { path: "/inventory?screen=acquisitions", tab: "inventory", label };
  if (["asset_movement", "location", "tag"].includes(entityType)) return { path: "/inventory", tab: "inventory", label };

  // A food with an id now has a page of its own to land on (#1045) — an audit
  // toast about a product should open the product, not the tab it lives in.
  if (entityType === "product" && id) return { path: `/groceries/foods/${routeId(id)}`, tab: "groceries", label };
  if (["product", "product_barcode", "stock_lot", "pantry_rule", "unit"].includes(entityType)) {
    return { path: "/groceries", tab: "groceries", label };
  }

  if (entityType === "recipe" && id) return { path: `/recipes/${routeId(id)}`, tab: "recipes", label };
  if (["recipe", "recipe_source", "recipe_ingredient", "imported_recipe", "imported_recipe_ingredient"].includes(entityType)) {
    return { path: "/recipes", tab: "recipes", label };
  }
  if (["meal_plan", "meal_plan_entry", "meal_template", "meal_template_ingredient", "simple_meal", "simple_meal_ingredient", "cooked_meal"].includes(entityType)) {
    return { path: "/meal-assembler", tab: "meal-assembler", label };
  }

  if (entityType === "shopping_list" && id) return detailTarget("shoppingList", id, label);
  if (entityType === "shopping_item") {
    return detailTarget("shoppingList", metadataId(metadata, "listId", "shoppingListId"), label);
  }
  if (entityType === "shopping_trip" || entityType === "shopping_trip_item") return detailTarget("shoppingTrip", null, label);

  if (entityType === "task" && id) return detailTarget("chore", id, label);
  if (["task_occurrence", "task_event", "chore", "chore_template", "chore_category"].includes(entityType)) {
    return detailTarget("chore", metadataId(metadata, "taskId", "sourceTaskId"), label);
  }

  if (entityType === "project" && id) return detailTarget("project", id, label);
  if (entityType === "markdown_document" && id) {
    // Household-level docs (no projectId) open straight from the docs hash; the
    // Projects view resolves the doc without needing a project selected.
    const projectId = metadataId(metadata, "projectId");
    const target = detailTarget("project", projectId, "document");
    return { ...target, path: `${target.path}#project-doc-${routeId(id)}` };
  }
  // Legacy project sub-entities (work items, materials, permits, …) from old
  // audit rows still land on the owning project.
  if (entityType.startsWith("project_") || entityType === "vendor") {
    return detailTarget("project", metadataId(metadata, "projectId", "renovationProjectId"), label);
  }

  // Ingestion entities (jobs, parsed documents, review items, attachments, …) no longer
  // have a dedicated page; their audit entries render as plain text.
  //
  // A document CITATION is the exception (#1178). When the assistant answers
  // out of the text inside a manual, the answer has to be able to open that
  // manual — and an attachment's destination is the thing it hangs off with the
  // file marked (`?attachment=`, #1175), which no amount of attachment id can
  // reconstruct. The server ships the path it built from the resolved parent;
  // this re-checks its exact shape rather than trusting a path, and returns
  // null without one so a citation renders no chip instead of a dead one.
  if (entityType === "attachment") {
    const routePath = typeof metadata?.routePath === "string" ? metadata.routePath : "";
    const cited = /^\/inventory\/assets\/[A-Za-z0-9_-]{1,64}\?attachment=(\d{1,12})$/.exec(routePath);
    // Shape AND identity: a well-formed route naming a different attachment
    // would open the wrong document, which is worse than opening nothing.
    return cited && cited[1] === id
      ? { path: routePath, tab: "inventory", label: "document" }
      : null;
  }

  if (["floor_plan", "floor_plan_revision", "floor_plan_source", "floor_level", "floor_room", "floor_zone", "layout_asset", "layout_asset_revision"].includes(entityType)) {
    return { path: "/floor-plan", tab: "floor-plan", label };
  }

  // The garden module (#888). A bed is a sidecar on a floor-plan room, but
  // its own audit rows belong to the garden screen that writes them —
  // `garden_bed` deliberately included, because deleting the ROOM is a
  // floor-plan event and unmapping the BED is a garden one.
  // A variety event opens the catalog SCREEN, which is the module's whole
  // deep-link contract (`?screen=`, read on mount and on popstate). No per-row
  // anchor: the list renders after its fetch, so a hash would be a promise the
  // page cannot keep — the same reason the vendor directory above has none.
  if (entityType === "plant_variety") return { path: "/garden?screen=catalog", tab: "garden", label };
  if (["garden_bed", "garden_planting", "garden_season", "garden_settings"].includes(entityType)) {
    return { path: "/garden", tab: "garden", label };
  }

  if (["device", "device_capability", "device_reading", "device_command", "automation_rule", "automation_run", "device_alert", "connector_health_event", "integration_connector"].includes(entityType)) {
    return { path: "/bms", tab: "bms", label };
  }

  if (entityType.startsWith("network_") || entityType === "wifi_network") return { path: "/network", tab: "network", label };

  // Finance chips open the section they mean (#1681): an uncategorised
  // transaction lands on the list, a budget alert on Budgets. Categories and
  // merchants have no section of their own and open Overview.
  if (["transaction", "finance_transaction", "transaction_split"].includes(entityType)) {
    return { path: financeSectionPath("transactions"), tab: "finance", label };
  }
  if (entityType === "finance_account") return { path: financeSectionPath("accounts"), tab: "finance", label };
  if (entityType === "budget") return { path: financeSectionPath("budgets"), tab: "finance", label };
  if (entityType === "recurring_item") return { path: financeSectionPath("recurring"), tab: "finance", label };
  if (["finance_category", "merchant"].includes(entityType)) {
    return { path: "/finance", tab: "finance", label };
  }

  if (entityType.startsWith("agent_")) return { path: "/assistant", tab: "assistant", label };
  if (entityType === "release_note") {
    const version = metadataId(metadata, "version");
    return {
      path: version ? `/release-notes/${routeId(version)}` : "/release-notes",
      tab: "release-notes",
      label: "release notes"
    };
  }
  if (["household", "user", "household_membership", "household_invitation", "signup_code", "google_settings", "google_backup_run"].includes(entityType)) {
    return { path: "/settings", tab: "settings", label };
  }

  return null;
}

export function routeForAuditEvent(event: AuditEvent): EntityRouteTarget | null {
  return routeForEntity(event.entityType, event.entityId ?? null, event.metadata);
}

/**
 * A toast deep link for an entity, labelled for humans (#586). `routeForEntity`
 * labels by entity type ("shopping item"), which reads as jargon in link text —
 * callers pass what the user just named instead ("BJs Shopping"). Returns
 * undefined rather than null so it can be spread into an optional field.
 */
export function toastLinkForEntity(
  label: string,
  entityType: string,
  entityId?: EntityReferenceId | null,
  metadata?: Record<string, unknown>
): EntityRouteTarget | undefined {
  if (!label.trim()) return undefined;
  const target = routeForEntity(entityType, entityId, metadata);
  return target ? { ...target, label } : undefined;
}

export function openEntityRoute(target: EntityRouteTarget): void {
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (current !== target.path) window.history.pushState(null, "", target.path);
  window.dispatchEvent(new PopStateEvent("popstate"));
  window.scrollTo({ top: 0, left: 0, behavior: "auto" });
}
